import {AppState} from "../RootReducer";
import {AppContext, MaintenanceCategory, MaintenanceWindow, TransferLimit} from "../http/protocol";
import {createSelector} from "reselect";
import {getUserAttributes} from "./user-selectors";
import {getDateAsNumber} from "../user/ui/utils";
import {SettingsState} from "../settings/SettingsReducer";

export const getOnrampCurrencies = (state : AppState) => state.settings.settings?.onramperCurrencies;

export const getSettings = (state : AppState) => state.settings.settings;

export const getCountry = (state : AppState) => state.settings.country;
export const getCountryWithFallback = (state : AppState) => state.user?.user?.country ?? state.settings?.country;
export const getJurisdiction = (state : AppState) => state.settings.jurisdictionId;
export const getSettingsState = (state : AppState) => state.settings;
export const getTransferLimits = (state : AppState)  : TransferLimit[] => {
    return state.settings.settings?.transferLimits ?? [];
}
export const getAppContext = (state : AppState) => state.settings.appContext;
export const getIsAppContext = (appContext: AppContext) => createSelector([getAppContext], (context) => context === appContext);
export const getIsAppContextNotNull = (state: AppState) => getAppContext(state) != null;
export const getIsMayaMiniAppContext = (state: AppState) => getIsAppContext(AppContext.MAYAMINIAPP)(state);
export const getIsGlifeContext = (state: AppState) => getIsAppContext(AppContext.GLIFE)(state);

function getActiveMaintenanceForCategory(settings: SettingsState, category: MaintenanceCategory) {
    const cat = settings.maintenance?.productMaintenance?.find((m: MaintenanceWindow) => m.category === category);
    if (cat && cat.enabled && hasMaintenanceStarted(cat, settings.serverTimeOffset)) {
        return cat;
    }
    return undefined;
}

export const getActiveCasinoMaintenance = createSelector([getSettingsState],(settings) : MaintenanceWindow | undefined => {
    let category = MaintenanceCategory.CASINO;
    return getActiveMaintenanceForCategory(settings, category);
});

const getCategory = (_:any, category : MaintenanceCategory) => category;
export const getMaintenanceAnnounceForCategory = createSelector([getSettingsState, getCategory],(settings, category) : MaintenanceWindow | undefined => {
    const cat = settings.maintenance?.productMaintenance?.find((m: MaintenanceWindow) => m.category === category);
    if (cat && cat.enabled && hasMaintenanceBeenAnnounced(cat, settings.serverTimeOffset)) {
        return cat;
    }
    return undefined;
});
export const getActiveSportsbookMaintenance = createSelector([getSettingsState],(settings) : MaintenanceWindow | undefined => {
    let category = MaintenanceCategory.SPORTSBOOK;
    return getActiveMaintenanceForCategory(settings, category);
});

export const getActivePokerMaintenance = createSelector([getSettingsState],(settings) : MaintenanceWindow | undefined => {
    let category = MaintenanceCategory.POKER;
    return getActiveMaintenanceForCategory(settings, category);
});

export const getActiveRacingMaintenance = createSelector([getSettingsState],(settings) : MaintenanceWindow | undefined => {
    let category = MaintenanceCategory.RACING;
    return getActiveMaintenanceForCategory(settings, category);
});
const hasMaintenanceBeenAnnounced = (mw? : MaintenanceWindow, serverTimeOffset? : number) => {
    let now = Date.now() - (serverTimeOffset ?? 0);
    let announceTime = getDateAsNumber(mw?.announceBeforeStart);
    if(announceTime) {
        return now > announceTime;
    }
    return false;
}
const hasMaintenanceStarted = (mw? : MaintenanceWindow, serverTimeOffset? : number) => {
    let now = Date.now() - (serverTimeOffset ?? 0);
    let announceTime = getDateAsNumber(mw?.start);
    if(announceTime) {
        return now > announceTime;
    }
    return false;
}

export const getCurrencyDefinitions = (state : AppState) => state.settings.settings?.currencies ?? [];

export const _getCurrencyCode = (_:any, code : string) => code;

export const getCurrencyDefinitionByCode = createSelector([getCurrencyDefinitions, _getCurrencyCode], (currencies, code)=> {
    return currencies.find(c => c.code === code);
})


export const getFiatExchangeCurrencies = createSelector([getCurrencyDefinitions], (currencies) => {
    currencies = currencies.filter(c => c.cryptoCurrency && !!c.cryptoPrices);
    let fiats : string[] =  [];
    currencies.forEach(c => c.cryptoPrices && Object.keys(c.cryptoPrices).forEach(code => {
        if(fiats.indexOf(code) == -1) {
            fiats.push(code)
        }
    }));
    return fiats;
})
export const showFiatValue = createSelector([getUserAttributes],(attributes)=>{
    return attributes?.displayFiatExchangeIfAvailable === "true";
})
export const getPreferredFiatExchange = createSelector([getCurrencyDefinitions, getUserAttributes, getSettings], (currencies, attributes, settings) => {
    let preferred : string |undefined;

    if(attributes.preferredFiatExchangeCurrency) {
       preferred =  attributes.preferredFiatExchangeCurrency;
    }
    if(settings?.defaultFiatExchangeCurrency && (!preferred || preferred.length == 0) ) {
        preferred=settings.defaultFiatExchangeCurrency;
    }

    currencies = currencies.filter(c => c.cryptoCurrency && !!c.cryptoPrices);
    let fiats : string[] =  [];
    currencies.forEach(c => c.cryptoPrices && Object.keys(c.cryptoPrices).forEach(code => {
        if(fiats.indexOf(code) == -1) {
            fiats.push(code)
        }
    }));

    if(preferred) {
        let found = fiats.find(c => c == preferred);
        if(found) {
            return found;
        }

    }

    return fiats.length > 0 ? fiats[0] : "USD";
})



export const selectTransactionSettings = createSelector([getSettingsState], (settings) => ({
    pokerEnabled: settings.settings?.pokerEnabled,
    casinoEnabled: settings.settings?.casinoEnabled,
    sportsbookEnabled: settings.settings?.sportsbookEnabled,
    racingEnabled: settings.settings?.racingEnabled,
    bundleBonusCurrency: settings.settings?.bundleBonusCurrency,
    sportsbookProviderConfig: settings.settings?.sportsbookConfig,
  })
);

